import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getFlow } from '../lib/api';
import { useClusterFlow } from '../hooks/useClusterFlow';
import { useClusterStore } from '../stores/clusterStore';
import { ACCENT } from '../styles/tokens';
import { useSelectedIdStore } from '../stores/selectedIdStore';
import { usePaletteStore } from '../stores/paletteStore';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { NodeList } from '../components/inspector/NodeList';
import { NodePanel } from '../components/inspector/NodePanel';
import type { NodeInfo, TaskInfo } from '../lib/types';

export default function NodesRoute() {
  const slug = useClusterStore(s => s.activeSlug);

  const enabled = slug !== 'all';
  const stream = useClusterFlow(enabled ? slug : null);
  const initial = useQuery({
    queryKey: ['flow', slug],
    queryFn: () => getFlow(slug),
    enabled,
    staleTime: 30_000,
  });

  const nodes: NodeInfo[] = useMemo(() => {
    if (stream.data?.flow?.nodes) return stream.data.flow.nodes;
    if (initial.data?.nodes) return initial.data.nodes;
    return [];
  }, [stream.data, initial.data]);

  const tasks: TaskInfo[] = useMemo(() => {
    if (stream.data?.flow?.tasks) return stream.data.flow.tasks;
    if (initial.data?.tasks) return initial.data.tasks;
    return [];
  }, [stream.data, initial.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const pending = useSelectedIdStore.getState().consume('nodes');
    if (pending && nodes.some(n => n.id === pending)) {
      setSelectedId(pending);
      return;
    }
    if (selectedId === null && nodes.length > 0) {
      setSelectedId(nodes[0].id);
      return;
    }
    if (
      selectedId !== null &&
      nodes.length > 0 &&
      !nodes.some(n => n.id === selectedId)
    ) {
      setSelectedId(nodes[0].id);
    }
  }, [nodes, selectedId]);

  useKeyboardNav({
    list: nodes,
    selectedId,
    setSelectedId,
    section: 'nodes',
    onPaletteToggle: () => usePaletteStore.getState().toggle(),
  });

  const selected = nodes.find(n => n.id === selectedId) ?? null;

  if (slug === 'all') {
    return <Navigate to="/overview" replace />;
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <NodeList
          nodes={nodes}
          tasks={tasks}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      </div>
      <aside className="w-[460px] flex-shrink-0 overflow-hidden border-l border-border bg-surface">
        {selected ? (
          <NodePanel item={selected} tasks={tasks} accent={ACCENT} />
        ) : (
          <div className="flex h-full items-center justify-center px-4 text-center text-[12px] text-textMute">
            {initial.isLoading || stream.status === 'connecting'
              ? 'Carregando nodes…'
              : 'Nenhum node disponível para este cluster.'}
          </div>
        )}
      </aside>
    </div>
  );
}
